import _extends from '@babel/runtime/helpers/esm/extends';
import _slicedToArray from '@babel/runtime/helpers/esm/slicedToArray';
import _objectWithoutProperties from '@babel/runtime/helpers/esm/objectWithoutProperties';
import _defineProperty from '@babel/runtime/helpers/esm/defineProperty';
import _styled from '@emotion/styled-base';
import 'core-js/modules/es6.object.to-string.js';
import 'core-js/modules/es6.array.iterator.js';
import 'core-js/modules/web.dom.iterable.js';
import React__default, { useState, useRef, useEffect } from 'react';
import { IconOnlyButton } from '../icon-only-button/icon-only-button.js';
import { HTMLHeading } from '../html-heading/html-heading.js';
import { ChevronLeft32, ChevronRight32 } from '@carbon/icons-react';
import { v4 } from 'uuid';
import { backgroundMidtone, borderDivider, borderRadiusSmall } from '../theme/theme.js';
import useCombinedRefs from '../../utils/use-combined-refs.js';

var _excluded = ["bodyContent", "heading", "headingTagLevel", "id", "initOpen", "width", "onOpenChange", "onWidthChange", "open", "t", "triggerButtonCollapseTooltipText", "triggerButtonExpandTooltipText"];

var _defaultTranslation;
var COLLAPSED_WIDTH = 24;
var MIN_WIDTH = 224;
var MAX_WIDTH = 480;
var DEFAULT_WIDTH = 288;
var TranslationKeys;

(function (TranslationKeys) {
  TranslationKeys["CollapsibleSidebarTriggerButtonCollapseTooltipText"] = "varicent-design-system.vds-collapsible-sidebar.vds-collapsible-sidebar-trigger-button-collapse-tooltip-text";
  TranslationKeys["CollapsibleSidebarTriggerButtonExpandTooltipText"] = "varicent-design-system.vds-collapsible-sidebar.vds-collapsible-sidebar-trigger-button-expand-tooltip-text";
})(TranslationKeys || (TranslationKeys = {}));

var defaultTranslation = (_defaultTranslation = {}, _defineProperty(_defaultTranslation, TranslationKeys.CollapsibleSidebarTriggerButtonCollapseTooltipText, function () {
  return "Collapse sidebar";
}), _defineProperty(_defaultTranslation, TranslationKeys.CollapsibleSidebarTriggerButtonExpandTooltipText, function () {
  return "Expand sidebar";
}), _defaultTranslation);

var defaultTranslate = function defaultTranslate(arg) {
  return defaultTranslation[arg.id](arg.values);
};

var CollapsibleSidebarWrapper = /*#__PURE__*/_styled("div", {
  target: "e1kk3iut0"
})("flex-shrink:0;position:relative;max-width:", "".concat(MAX_WIDTH, "px"), ";min-width:", "".concat(COLLAPSED_WIDTH, "px"), ";width:", function (_ref) {
  var open = _ref.open,
      dragging = _ref.dragging,
      width = _ref.width;

  if (width === undefined) {
    return "".concat(DEFAULT_WIDTH, "px");
  }

  if (!open && !dragging) {
    return "".concat(COLLAPSED_WIDTH, "px");
  }

  if (width >= MIN_WIDTH) {
    return "".concat(width, "px");
  }

  if (width < MIN_WIDTH) {
    if (width >= MIN_WIDTH / 2) {
      return "".concat(MIN_WIDTH, "px");
    } else {
      return "".concat(COLLAPSED_WIDTH, "px");
    }
  }

  return "".concat(COLLAPSED_WIDTH, "px");
}, ";height:100%;transition:", function (_ref2) {
  var dragging = _ref2.dragging,
      width = _ref2.width;
  return (!dragging || width <= MIN_WIDTH) && "width 200ms";
}, ";background-color:", backgroundMidtone, ";box-shadow:1px 0 ", borderDivider, ";cursor:", function (_ref3) {
  var dragging = _ref3.dragging,
      width = _ref3.width;
  return dragging && width >= MIN_WIDTH ? "col-resize" : dragging && "e-resize";
}, ";.vds-collapsible-sidebar-content{visibility:", function (_ref4) {
  var dragging = _ref4.dragging,
      open = _ref4.open;
  return !open && !dragging && "hidden";
}, ";opacity:", function (_ref5) {
  var dragging = _ref5.dragging,
      open = _ref5.open;
  return !open && !dragging && 0;
}, ";transition-property:opacity,visibility;transition-duration:200ms,0ms;transition-delay:", function (_ref6) {
  var open = _ref6.open;
  return !open && "0ms, 200ms";
}, ";& > *{pointer-events:", function (_ref7) {
  var dragging = _ref7.dragging;
  return dragging && "none";
}, ";}}");

var CollapsibleSidebarTriggerButtonWrapper = /*#__PURE__*/_styled("div", {
  target: "e1kk3iut1"
})("position:absolute;z-index:2;top:1.625rem;right:-1rem;background-color:", backgroundMidtone, ";border-radius:", borderRadiusSmall, ";");

var CollapsibleSidebarContent = /*#__PURE__*/_styled("div", {
  target: "e1kk3iut2"
})({
  name: "1b3t87l",
  styles: "display:flex;flex-direction:column;gap:1rem;height:100%;overflow-x:hidden;overflow-y:auto;"
});

var CollapsibleSidebarHeader = /*#__PURE__*/_styled("div", {
  target: "e1kk3iut3"
})("min-width:", "".concat(MIN_WIDTH, "px"), ";padding:1.625rem 1.5rem 0;");

var CollapsibleSidebarBody = /*#__PURE__*/_styled("div", {
  target: "e1kk3iut4"
})("display:flex;flex-direction:column;gap:1rem;min-width:", "".concat(MIN_WIDTH, "px"), ";padding:0 1.5rem 1.5rem;");

var CollapsibleSidebarDragHandle = /*#__PURE__*/_styled("div", {
  target: "e1kk3iut5"
})("position:absolute;z-index:1;top:0;right:", function (_ref8) {
  var dragging = _ref8.dragging,
      width = _ref8.width;
  return dragging ? width > MIN_WIDTH ? "-3.5rem" : "-8.5rem" : "-0.375rem";
}, ";bottom:0;width:", function (_ref9) {
  var dragging = _ref9.dragging,
      width = _ref9.width;
  return dragging ? width > MIN_WIDTH ? "5rem" : "10rem" : "0.75rem";
}, ";cursor:", function (_ref10) {
  var width = _ref10.width;
  return width <= MIN_WIDTH ? "e-resize" : width >= MAX_WIDTH ? "w-resize" : "col-resize";
}, ";");

var CollapsibleSidebar = /*#__PURE__*/React__default.forwardRef(function (_ref11, ref) {
  var bodyContent = _ref11.bodyContent,
      heading = _ref11.heading,
      _ref11$headingTagLeve = _ref11.headingTagLevel,
      headingTagLevel = _ref11$headingTagLeve === void 0 ? "h3" : _ref11$headingTagLeve,
      id = _ref11.id,
      _ref11$initOpen = _ref11.initOpen,
      initOpen = _ref11$initOpen === void 0 ? true : _ref11$initOpen,
      width = _ref11.width,
      onOpenChange = _ref11.onOpenChange,
      onWidthChange = _ref11.onWidthChange,
      open = _ref11.open,
      _ref11$t = _ref11.t,
      t = _ref11$t === void 0 ? defaultTranslate : _ref11$t,
      triggerButtonCollapseTooltipText = _ref11.triggerButtonCollapseTooltipText,
      triggerButtonExpandTooltipText = _ref11.triggerButtonExpandTooltipText,
      props = _objectWithoutProperties(_ref11, _excluded);

  var _useState = useState(false),
      _useState2 = _slicedToArray(_useState, 2),
      dragging = _useState2[0],
      setDragging = _useState2[1];

  var _useState3 = useState(!!initOpen),
      _useState4 = _slicedToArray(_useState3, 2),
      openState = _useState4[0],
      setOpenState = _useState4[1];

  var _useState5 = useState(width !== null && width !== void 0 ? width : DEFAULT_WIDTH),
      _useState6 = _slicedToArray(_useState5, 2),
      initialSidebarWidth = _useState6[0],
      setInitialSidebarWidth = _useState6[1];

  var _useState7 = useState(initialSidebarWidth),
      _useState8 = _slicedToArray(_useState7, 2),
      updatedSidebarWidth = _useState8[0],
      setUpdatedSidebarWidth = _useState8[1];

  var _useState9 = useState(0),
      _useState10 = _slicedToArray(_useState9, 2),
      startPos = _useState10[0],
      setStartPos = _useState10[1];

  var collapsibleSidebarRef = useRef(null);
  var combinedRef = useCombinedRefs(collapsibleSidebarRef, ref);
  var sidebarId = id !== null && id !== void 0 ? id : v4();
  var realOpenState = open !== null && open !== void 0 ? open : openState;

  var openSidebar = function openSidebar() {
    if (updatedSidebarWidth === COLLAPSED_WIDTH) {
      setInitialSidebarWidth(width !== null && width !== void 0 ? width : DEFAULT_WIDTH);
      setUpdatedSidebarWidth(width !== null && width !== void 0 ? width : DEFAULT_WIDTH);
    }

    setOpenState(true);
    !openState && onOpenChange && onOpenChange(true);
  };

  var closeSidebar = function closeSidebar() {
    setInitialSidebarWidth(COLLAPSED_WIDTH);
    setUpdatedSidebarWidth(COLLAPSED_WIDTH);
    setOpenState(false);
    openState && onOpenChange && onOpenChange(false);
  };

  var startDrag = function startDrag(e) {
    setDragging(true);
    setStartPos(e.clientX);
  };

  var drag = function drag(e) {
    if (dragging) {
      var resizeAmount = e.pageX - startPos;
      var newSidebarWidth = initialSidebarWidth + resizeAmount;
      setUpdatedSidebarWidth(newSidebarWidth > MAX_WIDTH ? MAX_WIDTH : newSidebarWidth);
    }
  };

  var endDrag = function endDrag() {
    if (dragging) {
      setDragging(false);
      setStartPos(0);

      if (updatedSidebarWidth < MIN_WIDTH / 2) {
        setInitialSidebarWidth(COLLAPSED_WIDTH);
        closeSidebar();
      } else {
        updatedSidebarWidth <= MIN_WIDTH ? setInitialSidebarWidth(MIN_WIDTH) : setInitialSidebarWidth(updatedSidebarWidth);
        openSidebar();
        onWidthChange && onWidthChange(updatedSidebarWidth);
      }
    }
  };

  useEffect(function () {
    if (open !== undefined) {
      open ? openSidebar() : closeSidebar();
    }
  }, [open]);
  useEffect(function () {
    var _collapsibleSidebarRe, _collapsibleSidebarRe2;

    setInitialSidebarWidth(((_collapsibleSidebarRe = collapsibleSidebarRef.current) === null || _collapsibleSidebarRe === void 0 ? void 0 : _collapsibleSidebarRe.offsetWidth) || (width !== null && width !== void 0 ? width : DEFAULT_WIDTH));
    setUpdatedSidebarWidth(((_collapsibleSidebarRe2 = collapsibleSidebarRef.current) === null || _collapsibleSidebarRe2 === void 0 ? void 0 : _collapsibleSidebarRe2.offsetWidth) || (width !== null && width !== void 0 ? width : DEFAULT_WIDTH));
  }, []);
  useEffect(function () {
    document.addEventListener("mouseleave", endDrag);
    window.addEventListener("mouseup", endDrag);
    window.addEventListener("mouseleave", endDrag);
    window.addEventListener("contextmenu", endDrag);
    window.addEventListener("mousemove", drag);
    return function () {
      document.removeEventListener("mouseleave", endDrag);
      window.removeEventListener("mouseup", endDrag);
      window.removeEventListener("mouseleave", endDrag);
      window.removeEventListener("contextmenu", endDrag);
      window.removeEventListener("mousemove", drag);
    };
  }, [dragging, updatedSidebarWidth]);
  return /*#__PURE__*/React__default.createElement(CollapsibleSidebarWrapper, _extends({
    "data-test": id ? "collapsible-sidebar-".concat(id) : undefined,
    dragging: dragging,
    id: id,
    open: realOpenState,
    ref: combinedRef,
    width: updatedSidebarWidth
  }, props), /*#__PURE__*/React__default.createElement(CollapsibleSidebarTriggerButtonWrapper, null, /*#__PURE__*/React__default.createElement(IconOnlyButton, {
    "aria-controls": "vds-collapsible-sidebar-".concat(sidebarId),
    "aria-expanded": realOpenState,
    className: "vds-collapsible-sidebar-trigger-button",
    "data-test": id ? "collapsible-sidebar-trigger-".concat(id) : undefined,
    icon: updatedSidebarWidth >= MIN_WIDTH / 2 ? ChevronLeft32 : ChevronRight32,
    minimal: true,
    onClick: function onClick() {
      return realOpenState ? closeSidebar() : openSidebar();
    },
    text: realOpenState ? triggerButtonCollapseTooltipText ? triggerButtonCollapseTooltipText : t({
      id: TranslationKeys.CollapsibleSidebarTriggerButtonCollapseTooltipText
    }) : triggerButtonExpandTooltipText ? triggerButtonExpandTooltipText : t({
      id: TranslationKeys.CollapsibleSidebarTriggerButtonExpandTooltipText
    })
  })), /*#__PURE__*/React__default.createElement(CollapsibleSidebarContent, {
    className: "vds-collapsible-sidebar-content",
    id: "vds-collapsible-sidebar-".concat(sidebarId)
  }, /*#__PURE__*/React__default.createElement(CollapsibleSidebarHeader, {
    className: "vds-collapsible-sidebar-header"
  }, heading && /*#__PURE__*/React__default.createElement(HTMLHeading, {
    bold: true,
    styleLevel: "h3",
    tagLevel: headingTagLevel,
    text: heading
  })), /*#__PURE__*/React__default.createElement(CollapsibleSidebarBody, {
    className: "vds-collapsible-sidebar-body"
  }, bodyContent && bodyContent)), /*#__PURE__*/React__default.createElement(CollapsibleSidebarDragHandle, {
    className: "vds-collapsible-sidebar-drag-handel",
    "data-test": id ? "collapsible-sidebar-drag-handle-".concat(id) : undefined,
    dragging: dragging,
    onMouseDown: function onMouseDown(e) {
      e.preventDefault();
      startDrag(e);
    },
    width: updatedSidebarWidth
  }));
});

export { CollapsibleSidebar };
//# sourceMappingURL=collapsible-sidebar.js.map
